import { CommonModule } from "@angular/common";
import { Component, OnInit, inject, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { trigger, transition, style, animate } from "@angular/animations";
import { ToasService } from "../service/toas-service.service";
import { ToastInsideConfig, PositionToast, ToastPositions } from "../toast.config";
import { ToastAlertComponent } from "../toast-alert.component";
import { IconComponent } from "../../icon/icon.component";
import { A11yModule } from "@angular/cdk/a11y";
import { NotificationService } from "src/app/core/service/notification.service";

@Component({
  selector: 'mg-toast-group',
  templateUrl: './toast-group.component.html',
  styleUrls: ['./toast-group.component.scss'],
  standalone: true,
  imports: [CommonModule, ToastAlertComponent, IconComponent, A11yModule],
  animations: [
    trigger('fadeButton', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('300ms ease-in', style({ opacity: 0 }))
      ])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToastGroupComponent implements OnInit {
  allToast: { [key: string]: ToastInsideConfig[] } = {
    'top-r': [], 'top-l': [], 'bottom-r': [], 'bottom-l': []
  };
  toastPositions: { [key: string]: PositionToast } = ToastPositions;
  showCloseButton = false;
  private toastQueue: ToastInsideConfig[] = [];
  private isQueueProcessing = false;
  private readonly _toastService = inject(ToasService);
  private readonly _notificationService = inject(NotificationService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this._toastService.toastObs.subscribe({
      next: (response) => {
        this.addToQueue(Object.values(response));
        this.cdr.markForCheck();
      }
    });

    this._toastService.flushQueueObs.subscribe(() => {
      this.toastQueue = [];
      this.isQueueProcessing = false;
      this.showCloseButton = false;
      this.clearAllToasts();
      this.cdr.markForCheck();
    });
  }

  async onToastClosed(data: ToastInsideConfig): Promise<void> {
    // Si el checkbox estaba marcado, marcar como leído
    if (data.isChecked && data.notificationId) {
      console.log(`Marcando notificación ${data.notificationId} como leída`);
      this._notificationService.markNotificationsAsRead([data.notificationId]).subscribe({
        next: () => console.log('Marcada como leída'),
        error: (err) => console.error('Error:', err)
      });
    } else {
      console.log(`Toast cerrado sin marcar: isChecked=${data.isChecked}, notificationId=${data.notificationId}`);
    }

    await this._toastService.deleteToast(data);
    this.allToast[data.position!] = this.allToast[data.position!].filter(
      t => t.count !== data.count
    );
    this.updateCloseButtonVisibility();
    this.cdr.markForCheck();
  }

  // Marcar todas las notificaciones como leídas
  async markAllAsRead(): Promise<void> {
    // Incluir tanto los toasts visibles como los de la cola
    const visibleNotifications = Object.values(this.allToast)
      .flat()
      .filter(toast => toast.notificationId);

    const queueNotifications = this.toastQueue
      .filter(toast => toast.notificationId);

    const allNotifications = [...visibleNotifications, ...queueNotifications];

    if (allNotifications.length === 0) return;

    // Marcar todos los checkboxes (tanto visibles como en cola)
    allNotifications.forEach(toast => {
      toast.isChecked = true;
    });
    this.cdr.markForCheck();

    // Obtener todos los IDs de notificaciones (sin duplicados)
    const notificationIds = Array.from(
      new Set(
        allNotifications
          .map(toast => toast.notificationId)
          .filter(id => id !== undefined)
      )
    ) as number[];

    // Llamar a la API para marcar todas como leídas
    if (notificationIds.length > 0) {
      this._notificationService.markNotificationsAsRead(notificationIds).subscribe({
        next: () => {
          // Cerrar todos los toasts y limpiar la cola
          this.closeAllNotifications();
        },
        error: (err) => {
          console.error('Error al marcar como leído:', err);
        }
      });
    } else {
      this.closeAllNotifications();
    }
  }

  async closeAllNotifications(): Promise<void> {
    // Obtener todas las notificaciones que tienen el check marcado
    const checkedNotifications = Object.values(this.allToast)
      .flat()
      .filter(toast => toast.isChecked && toast.notificationId)
      .map(toast => toast.notificationId)
      .filter(id => id !== undefined) as number[];

    // También incluir las de la cola que tengan check
    const checkedQueueNotifications = this.toastQueue
      .filter(toast => toast.isChecked && toast.notificationId)
      .map(toast => toast.notificationId)
      .filter(id => id !== undefined) as number[];

    const allCheckedIds = Array.from(
      new Set([...checkedNotifications, ...checkedQueueNotifications])
    );

    console.log('Cerrando todos - IDs con check:', allCheckedIds);

    // Llamar a la API solo si hay notificaciones con check
    if (allCheckedIds.length > 0) {
      this._notificationService.markNotificationsAsRead(allCheckedIds).subscribe({
        next: () => {
          console.log('Todas marcadas como leídas');
          this._toastService.closeAllToast();
        },
        error: (err) => {
          console.error('Error al marcar como leído:', err);
          this._toastService.closeAllToast();
        }
      });
    } else {
      console.log('Sin notificaciones con check');
      await this._toastService.closeAllToast();
    }
  }

  private addToQueue(toasts: ToastInsideConfig[]): void {
    toasts.forEach(t => {
      const existsInQueue = this.toastQueue.findIndex(i => i.count === t.count);
      const existsInAll = this.allToast[t.position!].findIndex(i => i.count === t.count);
      if (existsInQueue > -1) {
        this.toastQueue[existsInQueue] = t;
      } else if (existsInAll === -1) {
        this.toastQueue.push(t);
      } else if (t.bMostrar === false) {
        this.handleCloseToast(t);
      }
    });
    if (!this.isQueueProcessing) this.processQueue();
  }

  private processQueue(): void {
    if (this.toastQueue.length > 0) {
      this.isQueueProcessing = true;
      const toast = this.toastQueue.shift()!;
      this.initHandleToast(toast);
      setTimeout(() => this.processQueue(), 50);
    } else {
      this.isQueueProcessing = false;
    }
  }

  private initHandleToast(toast: ToastInsideConfig): void {
    if (!toast.bMostrar) return;
    if (!this.allToast[toast.position!].some(x => x.count === toast.count)) {
      this.allToast[toast.position!].push(toast);
      this.updateCloseButtonVisibility();
      this.cdr.markForCheck();
    }
  }

  private async handleCloseToast(data: ToastInsideConfig): Promise<void> {
    if (!data.bMostrar) {
      await this._toastService.deleteToast(data);
      this.allToast[data.position!] = this.allToast[data.position!].filter(
        t => t.count !== data.count
      );
      this.updateCloseButtonVisibility();
      this.cdr.markForCheck();
    }
  }

  private updateCloseButtonVisibility(): void {
    const hasToasts = Object.values(this.allToast).some(arr => arr.length > 0);
    this.showCloseButton = hasToasts;
  }

  private clearAllToasts(): void {
    for (const key in this.allToast) {
      this.allToast[key] = [];
    }
  }
}
